import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, ParkingCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setError('Invalid email address.');
      return;
    }
    setSending(true);
    try {
      await api.post('/contact', formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      }, 4000);
    } catch (err) {
      setError('Failed to send. Please try again later.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#04140e]">
      {/* Hero */}
      <section className="py-14 bg-[#faf6ec] text-[#0d3b25] text-center">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-3">Contact Us</h1>
          <p className="text-[#0d3b25]/70">We're here to help you. Reach out to us anytime.</p>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Contact info (dark card matching screenshot) */}
          <div className="bg-[#faf6ec] text-[#0d3b25] rounded-sm p-8" data-testid="contact-info-card">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">ADDRESS</p>
                  <p className="text-sm">Alfonse de Cockplein 5A<br/>1831 Diegem, Belgium</p>
                  <div className="mt-2 inline-flex items-center gap-2 bg-[#c5a059] text-[#0d3b25] font-semibold text-xs px-3 py-1 rounded-full" data-testid="parking-badge">
                    <ParkingCircle className="w-3.5 h-3.5" /> Free parking for 11 hrs
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">PHONE / WHATSAPP</p>
                  <a href="tel:+32473183927" className="text-sm hover:text-[#c5a059] transition-colors">+32 473 18 39 27</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">EMAIL</p>
                  <a href="mailto:info@4anhairandbeauty.be" className="text-sm hover:text-[#c5a059] transition-colors">info@4anhairandbeauty.be</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full border border-[#c5a059] flex items-center justify-center text-[#c5a059] flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-[10px] tracking-widest text-[#c5a059] mb-1">OPENING HOURS</p>
                  <p className="text-sm">10:00 – 19:00</p>
                  <p className="text-xs text-[#0d3b25]/60">Monday Closed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[#faf6ec] text-[#0d3b25] rounded-sm p-8" data-testid="contact-form-card">
            <h2 className="text-2xl font-serif font-bold text-[#c5a059] mb-6">Send a Message</h2>
            {submitted ? (
              <div className="bg-green-500/20 border border-green-400 rounded-sm p-6 text-center">
                <p className="text-white font-medium">Thank you for your message!</p>
                <p className="text-[#0d3b25]/70 text-sm mt-2">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-500/20 border border-red-400 text-white rounded-sm p-3 text-sm">
                    {error}
                  </div>
                )}
                <Input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your Name"
                  className="bg-transparent border-[#c5a059]/40 text-white placeholder:text-[#0d3b25]/50"
                  data-testid="contact-name-input"
                />
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email Address"
                  className="bg-transparent border-[#c5a059]/40 text-white placeholder:text-[#0d3b25]/50"
                  data-testid="contact-email-input"
                />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone Number"
                  className="bg-transparent border-[#c5a059]/40 text-white placeholder:text-[#0d3b25]/50"
                  data-testid="contact-phone-input"
                />
                <Textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Your Message"
                  rows={5}
                  className="bg-transparent border-[#c5a059]/40 text-white placeholder:text-[#0d3b25]/50"
                  data-testid="contact-message-input"
                />
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#c5a059] text-[#0d3b25] hover:bg-[#d4b56e] font-semibold rounded-sm"
                  data-testid="contact-submit-btn"
                >
                  {sending ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Google Maps */}
        <div className="max-w-6xl mx-auto px-6 mt-12" data-testid="contact-map-section">
          <h2 className="text-2xl font-serif font-bold text-[#0d3b25] mb-4 text-center">Find Us</h2>
          <div className="rounded-sm overflow-hidden border border-[#c5a059]/40 shadow-2xl">
            <iframe
              title="4AN Hair & Beauty Studio — Alfonse de Cockplein 5A, 1831 Diegem"
              src="https://www.google.com/maps?q=Alfonse+de+Cockplein+5A,+1831+Diegem,+Belgium&output=embed"
              width="100%"
              height="380"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-maps-iframe"
              allowFullScreen
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-3 justify-center items-center">
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Alfonse+de+Cockplein+5A,+1831+Diegem,+Belgium"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#c5a059] text-[#04140e] rounded-full px-5 py-2 text-sm hover:bg-[#d4b56e] transition-colors font-semibold"
              data-testid="get-directions-btn"
            >
              <MapPin className="w-4 h-4" /> Get Directions
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
